import React, { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "../../../utils/utilities";
import { retrieveLastProgress } from "../../../services/reportCache";
import { splitProject, SplitProjectPayload } from "../../../services/projectService";

import Button from "../../themed/button";
import Confirm from "../../themed/confirm";
import Modal from "../../themed/modal";

import useProjectDetails from "../../../hooks/useProjectDetails";
import routes from "../../../utils/routes";
import toastService from "../../../services/toastService";

const DuplicateReport: React.FC = () => {
    const [isConfirming, setConfirming] = useState(false);
    const navigate = useNavigate();

    const { projectDetails } = useProjectDetails();
    const { data, isFetching } =  useReportProgress(projectDetails?._id!);

    const duplicateReport = useCallback(async () => {
        if (!data || !data.data || !projectDetails) return;

        const payload: SplitProjectPayload = {
            projectId: projectDetails._id!,
            action: 'duplicate',
            data: [
                {
                    documentNumberPrefix: 'DUPLICATE-' + projectDetails?.documentNumber?.split('/')?.at(-1),
                    payload: JSON.stringify(data.data),
                },
            ],
        };

        const response = await splitProject(payload);
        
        navigate(routes.projectDetails.fullPath.replace(':projectId', response.data.project._id), {
            replace: true
        });
    }, [data, navigate, projectDetails]);

    const initiateDuplication = useCallback(() => {
        toastService.promise(duplicateReport(), {
			loading: 'Duplicating the report...',
			success: 'Report duplicated successfully!',
			error: getErrorMessage,
		});
    }, [duplicateReport]);

    const toggleConfirming = useCallback(() => {
        setConfirming((previous) => !previous);
    }, []);

    if (!projectDetails || !data || !data.data) {
        return null;
    }

    return (
        <>
            <Modal style={{ height: "auto" }} isVisible={isConfirming} onClose={toggleConfirming}>
                <div className="p-2">
                    <Confirm
                        title="Confirm"
                        message="Are you sure you want to duplicate this report?"
                        onCancel={toggleConfirming}
                        onConfirm={() => initiateDuplication()}
                    />
                </div>
            </Modal>
    
            <div className="me-2">
                <Button
                    disabled={isFetching}
                    icon="clipboard-check-fill"
                    label="Duplicate Report"
                    style={{ color: '#000000', backgroundColor: '#f8cbad', marginTop: 0 }}
                    onClick={toggleConfirming}
                />
            </div>
        </>
    );
};

const useReportProgress = (projectId: string) => {
    return useQuery({
        queryKey: ['report-progress', projectId],
        queryFn: () => retrieveLastProgress(projectId),
        refetchOnMount: 'always',
    });
};

export default DuplicateReport;